import { watchImmediate } from '@vueuse/core';
import { orbitControls } from '@/lib/threeScene/orbitControls';
import type { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import {
    EventDispatcher,
    MOUSE,
    type OrthographicCamera,
    Vector3,
} from 'three';
import { stopAll, type StopHandle, taggedLogger } from '@/util';
import { type RenderingContext } from '@/lib/threeScene/renderingState';
import LPS from '@/domain/anatomy/LPS';
import { floatsApproxEqual, vector3sApproxEqual } from '@/domain/geometry/approxEquals';
import { formatVector } from '@/domain/geometry/formatMath';

const log = taggedLogger('3D');

export type ThreeCamera = OrthographicCamera;

/**
 * The subset of camera-state that can be animated
 */
type _AnimatedState = {
    position: Vector3;
    up: Vector3;
    target: Vector3;
    zoom: number;
    near: number;
    far: number;

    /**
     * Frustum height, in mm. The frustum width is computed from this value and
     * the viewport aspect ratio.
     */
    height: number;
};

/** Identifies the change a click-and-drag with a mouse-button has on the camera */
export type MouseButtonMode = 'rotate' | 'zoom' | 'pan';

/**
 * Properties of a three-js [orthographic]{@link OrthographicCamera} camera.
 */
export type CameraState = _AnimatedState & {
    /** True if changes to the camera should be animated */
    animate: boolean;

    enableControls: boolean;

    /** Allow controls to rotate the camera */
    allowRotation: boolean;

    /** Allow controls to translate the camera */
    allowPan: boolean;

    /** Allow controls to zoom the camera */
    allowZoom: boolean;

    /** Control mode for the left-mouse-button. Defaults to 'rotate' */
    leftMouseButton: MouseButtonMode;

    /** Control mode for the middle-mouse-button. Defaults to 'zoom' */
    middleMouseButton: MouseButtonMode;

    /** Control mode for the right-mouse-button. Defaults to 'pan' */
    rightMouseButton: MouseButtonMode;
};

/** Default frustum height for an orthographic camera, in spatial scene-units (millimeters for 3D scenes) */
const _DEFAULT_ORTHOGRAPHIC_HEIGHT = 1000;

/** Default distance in scene-units (millimeters for 3D scenes) between a camera's look-at point and its position */
const _DEFAULT_CAMERA_DISTANCE = 450;

/**
 * Create the state of an orthographic camera.
 */
export function cameraState(properties?: Partial<CameraState>): CameraState {
    const target = properties?.target?.clone() ?? new Vector3();
    return {
        position:
            properties?.position?.clone() ??
            target.clone().add(LPS.Anterior.multiplyScalar(_DEFAULT_CAMERA_DISTANCE)),
        target,
        up: properties?.up ?? LPS.Superior,
        zoom: properties?.zoom ?? 1,
        near: properties?.near ?? 5,
        far: properties?.far ?? 10000,
        height: properties?.height ?? _DEFAULT_ORTHOGRAPHIC_HEIGHT,
        animate: properties?.animate ?? false,
        enableControls: properties?.enableControls ?? true,
        allowRotation: properties?.allowRotation ?? true,
        allowPan: properties?.allowPan ?? true,
        allowZoom: properties?.allowZoom ?? true,
        leftMouseButton: properties?.leftMouseButton ?? 'rotate',
        middleMouseButton: properties?.middleMouseButton ?? 'zoom',
        rightMouseButton: properties?.rightMouseButton ?? 'pan',
    };
}

export function updateCamera(context: RenderingContext): StopHandle {
    const controls: OrbitControls = orbitControls(context.canvas, context.camera);
    _applyNonAnimatedStateToThree(context, controls);
    _applyAnimatedStateToThree(context.state.camera, context.camera, controls);

    let animation: _Animation | null = null;
    let nextAnimationId: number = 1;
    let inInteraction: boolean = false;
    let suppressControlsChange: boolean = false;

    context.beforeRender(() => {
        if (animation) {
            if (_updateAnimation(context.camera, controls, animation) === 'finished') {
                animation = null;
            }
            // Changing the position or target of the camera in update-animation can
            // cause the controls-update to emit a 'change' event. We need to suppress this
            // because we want to continue the animation
            suppressControlsChange = true;
            controls.update();
            suppressControlsChange = false;
        } else {
            controls.update();
        }
    });

    return stopAll(
        watchImmediate(
            () => context.state.camera,
            () => {
                if (inInteraction) {
                    // The node has changed, but we are updating the node from the user interacting with
                    // the controls, so there is no change to apply
                    return;
                }
                _applyNonAnimatedStateToThree(context, controls);
                const currentState = _getAnimatedStateFromThree(context.camera, controls);
                if (_animatedStatesApproxEqual(currentState, context.state.camera)) {
                    // The node is still consistent with the camera and controls.
                    // This is typically the case when the user makes certain control changes
                    // e.g. mouse-wheel zoom
                    if (context.state.camera.animate) {
                        log.debug(_formatAnimatedState(
                            'Skipping animation: camera is already in the target state:',
                            currentState,
                        ));
                    }
                    return;
                }
                if (context.state.camera.animate) {
                    // Set an animation to apply the change
                    const targetState = _cloneAnimatedState(context.state.camera);
                    log.debug(_formatAnimatedState(
                        'Animating camera from current-state:',
                        currentState,
                    ));
                    log.debug(_formatAnimatedState(
                        'Animating camera to target-state:',
                        targetState,
                    ));
                    animation = {
                        id: nextAnimationId++,
                        initialTime: performance.now(),
                        duration: _ANIMATION_DURATION_MILLISECONDS,
                        initialState: currentState,
                        targetState,
                    };
                } else {
                    // Not animating, so apply the change directly
                    _applyAnimatedStateToThree(context.state.camera, context.camera, controls);
                }
            },
            { deep: true },
        ),
        _updateProjection(context),
        _addEventListener(controls, 'start', () => {
            log.debug('Starting camera user-interaction');
            // The user has started interaction, so begin setting the state from the camera and controls
            inInteraction = true;
            if (animation !== null) {
                log.debug('Camera user-interaction while camera is animating: aborting animation');
                animation = null;
            }
            _updateStateFromThree(context.state.camera, context.camera, controls);
        }),
        _addEventListener(controls, 'change', () => {
            if (!suppressControlsChange) {
                if (animation !== null) {
                    log.debug(
                        'Camera user-interaction while camera is animating: aborting animation',
                    );
                    animation = null;
                }
                _updateStateFromThree(context.state.camera, context.camera, controls);
            }
        }),
        _addEventListener(controls, 'end', () => {
            // The user has stopped interaction with the controls, so apply the changes to camera and
            // controls to the state
            log.debug('Camera user-interaction ended');
            inInteraction = false;
        }),
        () => controls.dispose(),
    );
}

function _updateProjection(context: RenderingContext): StopHandle {
    const camera = context.camera;
    return watchImmediate(
        () => [context.state.viewportSize, context.state.camera.height] as const,
        ([size, cameraHeight]) => {
            if (!size) {
                return;
            }
            const { width, height } = size;
            const frustumHeight = cameraHeight;
            const frustumWidth = (frustumHeight * width) / Math.max(height, 1);
            log.debug(
                'Updating orthographic-camera frustum width: %f, height: %f',
                frustumWidth,
                frustumHeight,
            );
            camera.left = -frustumWidth / 2;
            camera.right = frustumWidth / 2;
            camera.top = frustumHeight / 2;
            camera.bottom = -frustumHeight / 2;
            camera.updateProjectionMatrix();
        },
    );
}

/**
 * Set properties of the camera-state from the three-js camera and controls
 */
function _updateStateFromThree(
    state: CameraState,
    camera: OrthographicCamera,
    controls: OrbitControls,
): void {
    state.position.copy(camera.position);
    state.up.copy(camera.up);
    state.target.copy(controls.target);
    state.near = camera.near;
    state.far = camera.far;
    state.zoom = camera.zoom;
    state.height = camera.top - camera.bottom;
}

/**
 * Apply the given camera-state to the given camera and controls
 */
function _applyNonAnimatedStateToThree(context: RenderingContext, controls: OrbitControls) {
    const cameraState: CameraState = context.state.camera;

    // Avoid formatting unused debug messages if possible
    if (log.enabledFor('debug')) {
        log.debug(
            [
                'Apply non-animated state to camera:',
                `enable-controls: ${cameraState.enableControls}`,
                'enabled controls:',
                `  rotation: ${cameraState.allowRotation}`,
                `  zoom: ${cameraState.allowZoom}`,
                `  translation: ${cameraState.allowPan}`,
                'mouse-buttons:',
                `  left: ${cameraState.leftMouseButton}`,
                `  middle: ${cameraState.middleMouseButton}`,
                `  right: ${cameraState.rightMouseButton}`,
            ].join('\n  '),
        );
    }

    controls.enabled = cameraState.enableControls;
    controls.enableRotate = cameraState.allowRotation;
    controls.enableZoom = cameraState.allowZoom;
    controls.enablePan = cameraState.allowPan;

    function _getMouseAction(mode: MouseButtonMode | null): MOUSE | null {
        switch (mode) {
            case 'rotate':
                return MOUSE.ROTATE;
            case 'zoom':
                return MOUSE.DOLLY;
            case 'pan':
                return MOUSE.PAN;
            default:
                return null;
        }
    }

    controls.mouseButtons = {
        LEFT: _getMouseAction(cameraState.leftMouseButton),
        MIDDLE: _getMouseAction(cameraState.middleMouseButton),
        RIGHT: _getMouseAction(cameraState.rightMouseButton),
    };
}

const _ANIMATION_DURATION_MILLISECONDS = 400;

type _Animation = {
    id: number;
    initialTime: DOMHighResTimeStamp;
    duration: number;
    initialState: _AnimatedState;
    targetState: _AnimatedState;
};

/**
 * Copy properties of the three-js camera and controls as camera-state
 */
function _getAnimatedStateFromThree(
    camera: OrthographicCamera,
    controls: OrbitControls,
): _AnimatedState {
    return {
        position: camera.position.clone(),
        up: camera.up.clone(),
        target: controls.target.clone(),
        near: camera.near,
        far: camera.far,
        zoom: camera.zoom,
        height: camera.top - camera.bottom,
    };
}

/**
 * Return true if the animated camera-states are the same, within floating point precision
 */
function _updateAnimation(
    camera: OrthographicCamera,
    controls: OrbitControls,
    animation: _Animation,
): 'finished' | undefined {
    const elapsed = performance.now() - animation.initialTime;
    if (elapsed >= animation.duration) {
        log.debug('Finish camera animation %d', animation.id);
        _applyAnimatedStateToThree(animation.targetState, camera, controls);
        return 'finished';
    }

    const t = _easeOut(elapsed / animation.duration);
    log.debug('Updating camera animation %d', animation.id);
    _applyAnimatedStateToThree(
        _lerpAnimatedState(t, animation.initialState, animation.targetState),
        camera,
        controls,
    );
}

/**
 * Return true if the animated camera-states are the same, within floating point precision
 */
function _animatedStatesApproxEqual(state1: _AnimatedState, state2: _AnimatedState): boolean {
    // noinspection JSSuspiciousNameCombination
    return (
        vector3sApproxEqual(state1.position, state2.position) &&
        vector3sApproxEqual(state1.target, state2.target) &&
        vector3sApproxEqual(state1.up, state2.up) &&
        floatsApproxEqual(state1.far, state2.far) &&
        floatsApproxEqual(state1.near, state2.near) &&
        floatsApproxEqual(state1.zoom, state2.zoom) &&
        floatsApproxEqual(state1.height, state2.height)
    );
}

/**
 * Create a copy of animated-camera-state
 */
function _cloneAnimatedState(state: _AnimatedState): _AnimatedState {
    return {
        position: state.position.clone(),
        up: state.up.clone(),
        target: state.target.clone(),
        zoom: state.zoom,
        near: state.near,
        far: state.far,
        height: state.height,
    };
}

/**
 * Apply the given animated-camera-state to the given camera and controls
 */
function _applyAnimatedStateToThree(
    state: _AnimatedState,
    camera: OrthographicCamera,
    controls: OrbitControls,
) {
    // Avoid formatting unused debug messages if possible
    if (log.enabledFor('debug')) {
        log.debug(_formatAnimatedState('Apply animated state to camera:', state));
    }

    camera.position.copy(state.position);
    camera.up.copy(state.up);
    controls.target.copy(state.target);
    camera.near = state.near;
    camera.far = state.far;
    camera.zoom = state.zoom;
    // The orthographic camera's frustum is updated via the viewport watcher.
    camera.updateProjectionMatrix();
}

/**
 * Linearly interpolate animated-camera-state between initial and final values according to the
 * parameter t
 */
function _lerpAnimatedState(
    t: number,
    initialState: _AnimatedState,
    finalState: _AnimatedState,
): _AnimatedState {
    // noinspection JSSuspiciousNameCombination
    return {
        position: new Vector3().lerpVectors(initialState.position, finalState.position, t),
        up: new Vector3().lerpVectors(initialState.up, finalState.up, t),
        target: new Vector3().lerpVectors(initialState.target, finalState.target, t),
        zoom: _lerp(initialState.zoom, finalState.zoom, t),
        near: _lerp(initialState.near, finalState.near, t),
        far: _lerp(initialState.far, finalState.far, t),
        height: _lerp(initialState.height, finalState.height, t),
    };
}

/**
 * A standard linear-interpolation function, interpolating between values x0 and x1 according
 * to parameter t
 */
function _lerp(x0: number, x1: number, t: number): number {
    return x0 + t * (x1 - x0);
}

const _INTERPOLATION_FACTOR = 1.0 as const;
const _A = 4 + _INTERPOLATION_FACTOR;
const _B = _A / 3;

/**
 * This is an 'ease-out' interpolation function t -> x(t)
 *   - returns 0 if t is less than or equal to 0
 *   - returns 1 if t is greater than or equal to 1
 *   - otherwise interpolates between 0 and 1, in a way such that the value of x changes quickly
 *     at t = 0, x = 0 and eases slowly into t = 1, x = 1
 */
function _easeOut(t: number): number {
    if (t < 0) {
        return 0;
    } else if (t > 1) {
        return 1;
    } else {
        const u = 1 - t;
        return (
            Math.pow(t, 4) +
            4 * Math.pow(t, 3) * u +
            _A * Math.pow(t, 2) * Math.pow(u, 2) +
            _B * t * Math.pow(u, 3)
        );
    }
}

/**
 * Add a stoppable event-listener to a three-js event-dispatcher
 */
function _addEventListener(
    dispatcher: EventDispatcher,
    type: string,
    callback: () => void,
): StopHandle {
    dispatcher.addEventListener(type, callback);
    return () => {
        dispatcher.removeEventListener(type, callback);
    };
}

function _formatAnimatedState(message: string, state: _AnimatedState): string {
    return [
        message,
        `position: ${formatVector(state.position)}`,
        `up: ${formatVector(state.up)}`,
        `target: ${formatVector(state.target)}`,
        `near: ${state.near}`,
        `far: ${state.far}`,
        `zoom: ${state.zoom}`,
        `height: ${state.height}`,
    ].join('\n  ');
}
